import React from "react"
import { Box, Checkbox, DialogContentText, DialogTitle, FormControlLabel, Link } from "@carrotfertility/carotene-core"
import { ArrowSubmitButton, BackArrow, ReimbursementDialogFraming } from "../shared/layouts"
import { Progress } from "#/components/views/modal/Progress"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "#/components/context/reimbursement/GetReimbursedContext"
import { FormattedMessage, useIntl } from "react-intl"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { CountryCode } from "#/utils/CountryCode"

export const AttestationMedicallyNecessaryAndEligible = (): JSX.Element => {
  const { send, back } = useStateMachine()
  const { company } = useCurrentUser()
  const intl = useIntl()

  const { state, updateAttestationIsMedicallyNecessary, updateAttestationIsEligibilityRequirementMet, expenseId } =
    useReimbursementContext()

  const canSubmit = state.attestationIsMedicallyNecessary && state.attestationIsEligibilityRequirementMet

  // expenseRelatedTo has been translated before setting state
  const journeyType = state.expenseRelatedTo?.label?.toString().toLocaleLowerCase(intl.locale)

  const handleContinueClick = (): void => {
    send(company.countryCode === CountryCode.Australia ? "ATTESTATION_COVERED_BY_MEDICARE_AU" : "")
  }

  const goBack = (): void => {
    updateAttestationIsMedicallyNecessary(null)
    updateAttestationIsEligibilityRequirementMet(null)
    back()
  }

  const confirmationMessage = expenseId
    ? intl.formatMessage({
        defaultMessage: "We need your confirmation to process your expense in a legally compliant way."
      })
    : intl.formatMessage({
        defaultMessage: "We need your confirmation to process your reimbursement in a legally compliant way."
      })

  return (
    <>
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={goBack} />
      <ReimbursementDialogFraming>
        <DialogTitle>
          <FormattedMessage defaultMessage="Please confirm two things." />
        </DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.tokens.spacing.lg }}>
          <DialogContentText>{confirmationMessage}</DialogContentText>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" }
            }}
          >
            <FormControlLabel
              label={
                <FormattedMessage
                  defaultMessage="This expense is related to {journeyType} treatment recommended by my provider."
                  values={{
                    journeyType
                  }}
                />
              }
              control={
                <Checkbox
                  value="acknowledged"
                  required={true}
                  checked={state.attestationIsMedicallyNecessary}
                  onChange={(e) => {
                    updateAttestationIsMedicallyNecessary(e.target.checked)
                  }}
                  data-testid="checkbox-medically-necessary"
                />
              }
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" }
            }}
          >
            <FormControlLabel
              label={
                <FormattedMessage
                  defaultMessage="My provider meets Carrot’s <link>eligibility requirements</link>."
                  values={{
                    link: (linkContent) => (
                      <Link target="_blank" href={"/your-benefit-guide/provider-eligibility"} color="inherit">
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              }
              control={
                <Checkbox
                  value="acknowledged"
                  required={true}
                  checked={state.attestationIsEligibilityRequirementMet}
                  onChange={(e) => {
                    updateAttestationIsEligibilityRequirementMet(e.target.checked)
                  }}
                  data-testid="checkbox-requirements-met"
                />
              }
            />
          </Box>
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton enabled={canSubmit} onClick={handleContinueClick}>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </>
  )
}
